import * as Select from '@radix-ui/react-select'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Icon } from '../icon'
import { InputSelectClearButton } from './components/clear-button'
import { InputSelectHelperText } from './components/helper-text'
import { InputSelectIcon } from './components/icon'
import { InputSelectLabel } from './components/label'
import { InputSelectLeftIcon } from './components/left-icon'
import {
  SelectedVariants,
  clearAreaVariants,
  dropdownVariants,
  inputSelectVariants,
  optionVariants,
} from './input-select.styles'
import type { InputSelectProps } from './types'

const DISPLAY_NAME = 'InputSelect'

const InputSelectRoot = forwardRef<HTMLButtonElement, InputSelectProps>(
  (
    {
      label,
      leftIcon,
      options,
      disabled,
      readonly,
      error,
      size = 'md',
      onChange,
      hasFeedbackMessage = true,
      feedbackMessage,
      iconFeedback,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedValue, setSelectedValue] = useState<string | undefined>()
    const [resetKey, setResetKey] = useState(0)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const shouldDisplayFeedback =
      error || (hasFeedbackMessage && feedbackMessage)

    const onClear = (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation()
      setSelectedValue(undefined)
      setResetKey(prev => prev + 1)
      if (onChange) onChange(undefined)
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (triggerRef.current && contentRef.current) {
        const triggerWidth = triggerRef.current.offsetWidth
        contentRef.current.style.width = `${triggerWidth}px`
      }
    }, [isOpen])

    return (
      <div className="relative w-full">
        {label && (
          <InputSelectLabel size={size} disabled={disabled} readonly={readonly}>
            {label}
          </InputSelectLabel>
        )}

        <div className="relative w-full">
          <Select.Root
            key={resetKey}
            value={selectedValue}
            onValueChange={value => {
              if (value !== selectedValue) {
                setSelectedValue(value)
                if (onChange) onChange(value)
              }
            }}
            disabled={disabled || readonly}
            onOpenChange={setIsOpen}
          >
            <Select.Trigger
              ref={triggerRef}
              className={inputSelectVariants({
                size,
                isOpen,
                disabled: disabled,
                readonly: readonly,
                selected: !!selectedValue,
                error: !!error,
              })}
              {...rest}
            >
              <div className="flex w-full items-center justify-between gap-onepulse">
                {leftIcon && (
                  <InputSelectLeftIcon
                    icon={leftIcon}
                    size={size}
                    disabled={disabled}
                    readonly={readonly}
                  />
                )}
                <div
                  className={SelectedVariants({
                    size,
                    disabled,
                    readonly,
                  })}
                >
                  <Select.Value>
                    {selectedValue ? selectedValue : ''}
                  </Select.Value>
                </div>

                <div
                  className={clearAreaVariants({
                    size,
                  })}
                />

                <InputSelectIcon
                  size={size}
                  disabled={disabled}
                  readonly={readonly}
                  error={error}
                  isOpen={isOpen}
                />
              </div>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content
                className={dropdownVariants({
                  disabled,
                  readonly,
                  size,
                  margin: !!shouldDisplayFeedback,
                })}
                position="popper"
                ref={contentRef}
              >
                <Select.Viewport>
                  {options.map(option => (
                    <Select.Item
                      key={option}
                      value={option}
                      className={optionVariants({
                        disabled: false,
                      })}
                    >
                      <Select.ItemText>{option}</Select.ItemText>
                      {selectedValue === option && (
                        <Icon
                          color="colorTextSuccessAlternative"
                          symbol="rdicon-checkmark"
                        />
                      )}
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        <div className="flex">
          {selectedValue && !isOpen && !disabled && !readonly && (
            <InputSelectClearButton
              selectedValue={selectedValue}
              onClear={onClear}
              disabled={disabled}
              readonly={readonly}
              size={size}
              label={label ? size : false}
            />
          )}
        </div>

        {!!shouldDisplayFeedback && (
          <InputSelectHelperText
            icon={iconFeedback}
            error={error}
            disabled={disabled}
            readonly={readonly}
          >
            {error && !feedbackMessage ? 'Ocorreu um erro' : feedbackMessage}
          </InputSelectHelperText>
        )}
      </div>
    )
  }
)

InputSelectRoot.displayName = DISPLAY_NAME

export const InputSelect = {
  Root: InputSelectRoot,
  Label: InputSelectLabel,
  LeftIcon: InputSelectLeftIcon,
  ClearButton: InputSelectClearButton,
  HelperText: InputSelectHelperText,
}
